import { Injectable } from '@angular/core';

/**
 * Service for parsing JSONata expression strings into structured objects
 * Handles nested objects, arrays, and complex string concatenations
 */
@Injectable({
  providedIn: 'root'
})
export class JsonataParserService {

  /**
   * Parse JSONata expression string into a structured object for better display
   * Recursively parses nested objects, arrays, and complex string expressions
   * @param expression The JSONata expression string
   * @returns Structured object representation
   */
  parseJsonataExpression(expression: string): any { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!expression || typeof expression !== 'string') {
      return null;
    }

    const parseValue = (value: string): any => { // eslint-disable-line @typescript-eslint/no-explicit-any
      const trimmed = value.trim();

      if (!trimmed) {
        return trimmed;
      }

      // Try JSON parse first
      try {
        return JSON.parse(trimmed);
      } catch {
        // If not valid JSON, continue parsing
      }

      // String value (quoted)
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith('\'') && trimmed.endsWith('\''))) {
        return trimmed.slice(1, -1);
      }

      // Complex string concatenation (contains & operator)
      if (trimmed.includes(' & ') || trimmed.includes('&')) {
        return this.parseStringConcatenation(trimmed);
      }

      // Array
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        return this.parseArray(trimmed, parseValue);
      }

      // Object
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        return this.parseJsonataExpression(trimmed);
      }

      // Number
      if (!isNaN(Number(trimmed)) && trimmed.trim() !== '' && !trimmed.includes(' ')) {
        return Number(trimmed);
      }

      // Boolean
      if (trimmed === 'true' || trimmed === 'false') {
        return trimmed === 'true';
      }

      // Return as is (JSONata expression or variable reference)
      return trimmed;
    };

    const result: any = {}; // eslint-disable-line @typescript-eslint/no-explicit-any

    // Remove outer braces if present
    let cleaned = expression.trim();
    if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
      cleaned = cleaned.slice(1, -1).trim();
    }

    // Split by comma, but be careful with nested objects and arrays
    const parts = this.splitByTopLevelComma(cleaned);

    // Parse each part
    parts.forEach(part => {
      const colonIndex = part.indexOf(':');
      if (colonIndex > 0) {
        const key = part.substring(0, colonIndex).trim().replace(/^["']|["']$/g, '');
        const value = part.substring(colonIndex + 1).trim();
        if (key) {
          result[key] = parseValue(value);
        }
      }
    });

    return result;
  }

  /**
   * Parse string concatenation expressions (e.g., "text" & variable & "more text")
   * @param expression The concatenation expression
   * @returns Parsed string representation
   */
  private parseStringConcatenation(expression: string): string {
    // For display purposes, return the expression as-is
    // This preserves the JSONata syntax for readability
    return expression.trim();
  }

  /**
   * Parse array string into array structure
   * @param arrayString The array string
   * @param parseValue Function to parse individual values
   * @returns Parsed array
   */
  private parseArray(arrayString: string, parseValue: (value: string) => any): any[] { // eslint-disable-line @typescript-eslint/no-explicit-any
    const arrayContent = arrayString.slice(1, -1).trim();
    if (!arrayContent) {
      return [];
    }

    const items: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
    const parts = this.splitByTopLevelComma(arrayContent);

    parts.forEach(part => {
      if (part.trim()) {
        items.push(parseValue(part.trim()));
      }
    });

    return items;
  }

  /**
   * Split string by top-level commas (ignoring commas inside strings, objects, and arrays)
   * @param str The string to split
   * @returns Array of parts
   */
  private splitByTopLevelComma(str: string): string[] {
    const parts: string[] = [];
    let currentPart = '';
    let depth = 0;
    let inString = false;
    let stringChar = '';

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const prevChar = i > 0 ? str[i - 1] : '';

      // Handle string quotes
      if ((char === '"' || char === '\'') && prevChar !== '\\') {
        if (!inString) {
          inString = true;
          stringChar = char;
        } else if (char === stringChar) {
          inString = false;
          stringChar = '';
        }
      }

      if (!inString) {
        if (char === '{' || char === '[') {
          depth++;
        } else if (char === '}' || char === ']') {
          depth--;
        } else if (char === ',' && depth === 0) {
          parts.push(currentPart.trim());
          currentPart = '';
          continue;
        }
      }

      currentPart += char;
    }

    if (currentPart.trim()) {
      parts.push(currentPart.trim());
    }

    return parts;
  }
}

